import React from "react";
import { Link } from "react-router-dom";
import { websitePaths } from "../../../routes/websitePaths";
import { useFetchPublicProjectImagesQuery } from "../../../store/api/projectImagesApi";
import "../../../components/website/sections/ProjectCategoryPage.css";

const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500'%3E%3Crect width='800' height='500' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-size='18' font-family='sans-serif'%3EDialin24 Residential%3C/text%3E%3C/svg%3E";

type ResidentialProject = {
  title: string;
  category: string;
  location: string;
  description: string;
  features: string[];
  images: string[];
};

const categoryHighlights = [
  {
    title: "Villas And Premium Homes",
    description: "Beautiful custom homes planned for comfort, premium finishes, and long-term family living.",
    icon: "bi bi-house-heart",
  },
  {
    title: "Duplex And Family Homes",
    description: "Smart family homes with practical layouts, better ventilation, and efficient space planning.",
    icon: "bi bi-houses",
  },
  {
    title: "Apartment And Society Upgrades",
    description: "Flat upgrades and residential block improvements handled with smooth, well-managed execution.",
    icon: "bi bi-buildings",
  },
];

const residentialProjects: ResidentialProject[] = [
  {
    title: "Luxury Villa Construction",
    category: "Premium Villa",
    location: "Gomti Nagar, Lucknow",
    description:
      "Turnkey villa project with premium facade treatment, modular kitchen planning, landscaped entry, and smart lighting integration.",
    features: [
      "Double-height entrance and premium stone facade",
      "Integrated living, dining, and utility zoning",
      "Interior-ready handover with premium finishes",
    ],
    images: [
      "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    title: "Duplex Home Development",
    category: "Family Duplex",
    location: "Indira Nagar, Lucknow",
    description:
      "Contemporary duplex residence planned around family comfort, circulation clarity, daylight, and efficient room distribution.",
    features: [
      "Family lounge with open visual connection",
      "Natural light and ventilation-first planning",
      "Utility and staircase alignment for compact home sites",
    ],
    images: [
      "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80",
    ],
  },
  {
    title: "Apartment Block Upgrade",
    category: "Residential Upgrade",
    location: "Aliganj, Lucknow",
    description:
      "Structural strengthening, common-area improvement, and interior modernization for a multi-unit residential block.",
    features: [
      "Common circulation and staircase enhancement",
      "Flat interior refresh with coordinated execution",
      "Phased renovation with occupied-site planning",
    ],
    images: [
      "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=1200&q=80",
    ],
  },
];

const completedResidentialProjects = [
  {
    title: "Completed Residential Portfolio",
    location: "Lucknow And NCR",
    image: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Modern Duplex Handover",
    location: "Indira Nagar",
    image: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Villa Exterior Completion",
    location: "Gomti Nagar",
    image: "https://images.unsplash.com/photo-1600585154206-4b3d2f8c8f10?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Premium Interior Finish",
    location: "Lucknow",
    image: "https://images.unsplash.com/photo-1600210492493-0946911123ea?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Apartment Upgrade Completion",
    location: "Aliganj",
    image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80",
  },
  {
    title: "Family Living Space Delivery",
    location: "Noida",
    image: "https://images.unsplash.com/photo-1502005097973-6a7082348e28?auto=format&fit=crop&w=1200&q=80",
  },
];

const ResidentialProjects: React.FC = () => {
  const { data: apiImages = [] } = useFetchPublicProjectImagesQuery("Residential");

  const resolveImages = (fallbacks: string[], startIdx: number, count = 3): string[] => {
    const slice = apiImages.slice(startIdx, startIdx + count).map((img) => img.imageUrl);
    while (slice.length < count) slice.push(fallbacks[slice.length] ?? PLACEHOLDER);
    return slice;
  };

  const liveProjects = residentialProjects.map((p, i) => ({
    ...p,
    images: resolveImages(p.images, i * 3),
  }));

  const liveCompleted = completedResidentialProjects.map((item, i) => ({
    ...item,
    image: apiImages[i]?.imageUrl ?? item.image,
  }));

  return (
    <section className='project-category-page website-page'>
      <div className='project-category-hero website-page-hero text-center'>
        <div className='container'>
          <p className='website-page-kicker mb-1'>Residential Portfolio</p>
          <h1 className='project-page-title website-page-title mb-2'>
            Explore residential projects designed for comfort, quality, and modern family living.
          </h1>
          <p className='project-page-subtitle website-page-subtitle mx-auto mb-0'>
            Browse villas, duplex homes, and apartment upgrades with project highlights, image galleries, and completed
            work that helps customers understand our design and execution quality.
          </p>
        </div>
      </div>

      <div className='container website-page-content'>
        <div className='row g-4 mb-5'>
          {categoryHighlights.map((item) => (
            <div className='col-md-6 col-lg-4' key={item.title}>
              <article className='project-category-link-card h-100'>
                <div className='project-category-link-icon'>
                  <i className={item.icon}></i>
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            </div>
          ))}
        </div>

        <div className='project-residential-section-head mb-4'>
          <span className='website-section-kicker'>Project Types</span>
          <h2 className='website-section-title mb-2'>Choose the residential style that fits your home plan.</h2>
          <p className='website-section-subtitle mb-0'>
            Each section highlights a different residential project type with clear features, location details, and
            multiple project images.
          </p>
        </div>

        <div className='residential-project-list'>
          {liveProjects.map((project) => (
            <article className='residential-project-card' key={`${project.title}-${project.location}`}>
              <div className='residential-project-top'>
                <div>
                  <span className='residential-project-badge'>{project.category}</span>
                  <h3>{project.title}</h3>
                  <p className='residential-project-location'>
                    <i className='bi bi-geo-alt-fill me-2'></i>
                    {project.location}
                  </p>
                </div>
                <Link to={websitePaths.contact} className='btn brand-btn-primary'>
                  Discuss Similar Project
                </Link>
              </div>

              <div className='row g-4 align-items-start'>
                <div className='col-lg-7'>
                  <div className='residential-gallery-grid'>
                    <div className='residential-gallery-main'>
                      <img src={project.images[0]} alt={project.title} />
                    </div>
                    <div className='residential-gallery-stack'>
                      {project.images.slice(1).map((image, index) => (
                        <div className='residential-gallery-small' key={`${project.title}-${index}`}>
                          <img src={image} alt={`${project.title} view ${index + 2}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className='col-lg-5'>
                  <div className='residential-project-content'>
                    <p className='mb-3'>{project.description}</p>
                    <ul className='residential-project-features'>
                      {project.features.map((feature) => (
                        <li key={`${project.title}-${feature}`}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className='project-residential-section-head completed-projects-head'>
          <span className='website-section-kicker'>Completed Projects</span>
          <h2 className='website-section-title mb-2'>See completed residential projects delivered for our customers.</h2>
          <p className='website-section-subtitle mb-0'>
            This gallery showcases finished homes and upgrades so customers can quickly review our completed work and
            design quality.
          </p>
        </div>

        <div className='row g-4'>
          {liveCompleted.map((item) => (
            <div className='col-sm-6 col-lg-4' key={`${item.title}-${item.location}`}>
              <article className='completed-project-card'>
                <img src={item.image} alt={item.title} className='completed-project-image' />
                <div className='completed-project-overlay'>
                  <h4>{item.title}</h4>
                  <p>{item.location}</p>
                </div>
              </article>
            </div>
          ))}
        </div>

        <div className='project-category-cta mt-4'>
          <div>
            <span className='website-section-kicker'>Plan Your Home</span>
            <h3 className='mb-2'>Tell us about your home or renovation idea and our team will guide you.</h3>
            <p className='mb-0'>
              We help customers plan custom homes, duplex layouts, villa construction, and apartment upgrades with a
              clear execution path.
            </p>
          </div>
          <Link to={websitePaths.contact} className='btn brand-btn-primary'>
            Start Residential Consultation
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ResidentialProjects;



